import React, { useState } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { toast } from 'react-toastify';
import RichTextEditor from './RichTextEditor';
import { format } from 'date-fns';
import { CURRENCY_SYMBOLS } from '../utils/currencySymbols';
import { FiX } from 'react-icons/fi';
import { FiLoader } from 'react-icons/fi';

const ExpenseModal = ({
  onClose,
  onSubmit,
  categories,
  updateCategories,
  currencies,
  editingExpense,
  selectedDate,
  bulkMode = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: editingExpense?.title || '',
    amount: editingExpense?.amount ? Number(editingExpense.amount).toFixed(2) : '',
    category: editingExpense
      ? { value: editingExpense.category, label: editingExpense.category }
      : null,
    description: editingExpense?.description || '',
    currency: editingExpense?.currency || currencies[0],
    date: editingExpense?.date || format(selectedDate, 'yyyy-MM-dd'),
    id: editingExpense?.id || null,
  });

  const [bulkExpenses, setBulkExpenses] = useState([
    {
      title: '',
      amount: '',
      category: null,
      description: '',
      currency: currencies[0],
      date: format(selectedDate, 'yyyy-MM-dd'),
    },
  ]);

  const categoryOptions = categories
    .map(cat => ({ value: cat, label: cat }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const currencyOptions = currencies.map(curr => ({ 
    value: curr, 
    label: `${curr} (${CURRENCY_SYMBOLS[curr]})`
  }));

  const handleCreateCategory = async (inputValue) => {
    const newCategory = inputValue.trim();
    if (newCategory && !categories.includes(newCategory)) {
      try {
        const newCategories = [...categories, newCategory];
        await updateCategories(newCategories);
        
        setFormData(prev => ({
          ...prev,
          category: { value: newCategory, label: newCategory }
        }));
        
        toast.success('Category created successfully');
      } catch (error) {
        toast.error('Failed to create category');
      }
    }
  };

  const handleBulkChange = (idx, field, value) => {
    setBulkExpenses((prev) =>
      prev.map((row, i) =>
        i === idx ? { ...row, [field]: value } : row
      )
    );
  };

  const handleBulkCategoryChange = (idx, value) => {
    setBulkExpenses((prev) =>
      prev.map((row, i) =>
        i === idx ? { ...row, category: value } : row
      )
    );
  };

  const handleBulkCreateCategory = async (inputValue, idx) => {
    const newCategory = inputValue.trim();
    if (newCategory && !categories.includes(newCategory)) {
      try {
        const newCategories = [...categories, newCategory];
        await updateCategories(newCategories);
        setBulkExpenses((prev) =>
          prev.map((row, i) =>
            i === idx
              ? { ...row, category: { value: newCategory, label: newCategory } }
              : row
          )
        );
        toast.success('Category created successfully');
      } catch (error) {
        toast.error('Failed to create category');
      }
    }
  };

  const addBulkRow = () => {
    setBulkExpenses((prev) => [
      ...prev,
      {
        title: '',
        amount: '',
        category: null,
        description: '',
        currency: currencies[0],
        date: format(selectedDate, 'yyyy-MM-dd'),
      },
    ]);
  };

  const removeBulkRow = (idx) => {
    setBulkExpenses((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    
    const validRows = bulkExpenses.filter(
      (row) =>
        row.title.trim() &&
        row.amount &&
        row.category &&
        row.currency &&
        row.date
    );
    if (validRows.length === 0) {
      toast.error('Please fill in at least one complete expense.');
      return;
    }
    setIsLoading(true);
    try {
      const mapped = validRows.map((row) => ({
        ...row,
        title: row.title.trim(),
        category: row.category.value,
        amount: Number(row.amount).toFixed(2),
      }));
      await onSubmit(mapped);
    } catch (error) {
      toast.error('Failed to add expenses');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      await onSubmit({
        ...formData,
        title: formData.title.trim(),
        category: formData.category?.value || '',
        currency: formData.currency,
        date: formData.date
      });
    } catch (error) {
      toast.error('Failed to save expense');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAmountChange = (e) => {
    let value = e.target.value;
    
    // Remove any non-numeric characters except decimal point
    value = value.replace(/[^\d.]/g, '');
    
    // Ensure only one decimal point
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }

    setFormData(prev => ({ ...prev, amount: value }));
  };

  const customStyles = {
    input: (base) => ({
      ...base,
      'input:focus': {
        boxShadow: 'none',
      },
    }),
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#6366f1' : '#e5e7eb',
      boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
      '&:hover': {
        borderColor: '#6366f1',
      },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#6366f1' 
        : state.isFocused 
          ? '#e0e7ff' 
          : 'white',
      color: state.isSelected ? 'white' : '#374151',
      '&:active': {
        backgroundColor: '#6366f1',
      },
    }),
  };

  if (bulkMode) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl p-6 w-full max-w-3xl h-5/6 overflow-y-auto shadow-xl">
          <h3 className="text-xl font-semibold mb-6 text-gray-800 dark:text-gray-200">
            Add Bulk Expenses
          </h3>
          <form onSubmit={handleBulkSubmit} className="space-y-4">
            <div className="space-y-4">
              {bulkExpenses.map((row, idx) => (
                <div
                  key={idx}
                  className={`
                    relative p-4 rounded-xl border
                    ${idx % 2 === 0
                      ? 'bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-slate-800 dark:via-slate-900 dark:to-slate-800 border-gray-200 dark:border-gray-700'
                      : 'bg-gradient-to-br from-indigo-50 via-indigo-100 to-indigo-200 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 border-indigo-100 dark:border-indigo-900'
                    }
                    shadow-md transition hover:shadow-2xl
                  `}
                >
                  <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[140px] flex flex-col gap-1">
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Title</label>
                      <input
                        type="text"
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                        value={row.title}
                        onChange={(e) => handleBulkChange(idx, 'title', e.target.value)}
                        required
                        placeholder="Title"
                      />
                    </div>
                    <div className="w-28 min-w-[100px] flex flex-col gap-1">
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Currency</label>
                      <select
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                        value={row.currency}
                        onChange={(e) => handleBulkChange(idx, 'currency', e.target.value)}
                      >
                        {currencies.map((curr) => (
                          <option key={curr} value={curr}>
                            {curr}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="w-28 min-w-[100px] flex flex-col gap-1">
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                        value={row.amount}
                        onChange={(e) => handleBulkChange(idx, 'amount', e.target.value)}
                        required
                        placeholder="0.00"
                      />
                    </div>
                    <div className="flex-1 min-w-[140px] flex flex-col gap-1">
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Category</label>
                      <CreatableSelect
                        options={categories.map((cat) => ({
                          value: cat,
                          label: cat,
                        }))}
                        value={row.category}
                        onChange={(selected) => handleBulkCategoryChange(idx, selected)}
                        onCreateOption={(inputValue) => handleBulkCreateCategory(inputValue, idx)}
                        styles={customStyles}
                        placeholder="Category"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        formatCreateLabel={(inputValue) => `Create category "${inputValue}"`}
                        theme={(theme) => ({
                          ...theme,
                          borderRadius: 6,
                          colors: {
                            ...theme.colors,
                            primary25: 'var(--hover-bg)',
                            primary: 'var(--border-color)',
                            neutral0: 'var(--input-bg)',
                            neutral80: 'var(--text-primary)',
                            neutral20: 'var(--input-border)',
                            neutral30: 'var(--input-border)',
                          },
                        })}
                      />
                    </div>
                    <div className="w-40 min-w-[120px] flex flex-col gap-1">
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Date</label>
                      <input
                        type="date"
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                        value={row.date}
                        onChange={(e) => handleBulkChange(idx, 'date', e.target.value)}
                        required
                      />
                    </div>
                    <div className="flex-1 min-w-[140px] flex flex-col gap-1">
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Description</label>
                      <input
                        type="text"
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                        value={row.description}
                        onChange={(e) => handleBulkChange(idx, 'description', e.target.value)}
                        placeholder="Description"
                      />
                    </div>
                  </div>
                  {bulkExpenses.length > 1 && (
                    <button
                      type="button"
                      className="absolute -top-3 -right-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 shadow-md rounded-full p-2 flex items-center justify-center hover:bg-red-500 hover:text-white transition"
                      onClick={() => removeBulkRow(idx)}
                      title="Remove row"
                      style={{ boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)' }}
                    >
                      <FiX className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center mt-4">
              <button
                type="button"
                className="bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-200 px-4 py-2 rounded hover:bg-gray-300 dark:hover:bg-slate-600 transition"
                onClick={addBulkRow}
              >
                Add Row
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <FiLoader className="animate-spin" />
                      Adding...
                    </>
                  ) : (
                    'Add Expenses'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-xl h-2/3 2xl:h-max overflow-y-auto shadow-xl">
        <h3 className="text-xl font-semibold mb-6 text-gray-800 dark:text-gray-200">
          {editingExpense ? 'Edit Expense' : 'Add New Expense'}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-6">
          {editingExpense && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date
              </label>
              <input
                type="date"
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 
                  focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 
                  transition-colors duration-200
                  dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                required
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title
            </label>
            <input
              type="text"
              required
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Enter expense title"
            />
          </div>
          
          <div className="grid grid-cols-3 gap-4 items-center">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <Select
                options={currencyOptions}
                value={currencyOptions.find(opt => opt.value === formData.currency)}
                onChange={(selected) => setFormData(prev => ({ 
                  ...prev, 
                  currency: selected.value 
                }))}
                styles={customStyles}
                isSearchable={false}
              />
            </div>
            
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-gray-500">
                  {CURRENCY_SYMBOLS[formData.currency]}
                </span>
                <input
                  type="number"
                  required
                  step="0.01"
                  className="w-full pl-8 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                  value={formData.amount}
                  onChange={handleAmountChange}
                  onBlur={(e) => {
                    // Format to 2 decimal places when leaving the input
                    if (e.target.value && !isNaN(e.target.value)) {
                      setFormData(prev => ({
                        ...prev,
                        amount: Number(e.target.value).toFixed(2)
                      }));
                    }
                  }}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <CreatableSelect
              options={categoryOptions}
              value={formData.category}
              onChange={(selected) => setFormData(prev => ({ ...prev, category: selected }))}
              onCreateOption={handleCreateCategory}
              styles={customStyles}
              placeholder="Select or create a category"
              className="react-select-container"
              classNamePrefix="react-select"
              formatCreateLabel={(inputValue) => `Create category "${inputValue}"`}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <RichTextEditor
              content={formData.description}
              onChange={(newContent) => setFormData(prev => ({ 
                ...prev, 
                description: newContent 
              }))}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 dark:hover:text-gray-700 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <FiLoader className="animate-spin" />
                  {editingExpense ? 'Updating...' : 'Adding...'}
                </>
              ) : (
                editingExpense ? 'Update Expense' : 'Add Expense'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpenseModal; 